import { faArrowDownLeftAndArrowUpRightToCenter, faArrowLeftToLine, faArrowsFromDottedLine, faArrowsToDottedLine, faArrowUpRightAndArrowDownLeftFromCenter, faBellSlash, faBells, faCheck, faChevronDown, faEllipsis, faRedo, faThoughtBubble } from '@fortawesome/pro-regular-svg-icons';
import { type ChatMessage, type ChatPersona, ChatWindow } from '@tryhackme/thm-ui-components';
import type { ChatWindowProps } from '@tryhackme/thm-ui-components/dist/components/compounds/tutor/chat-window.types';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useAppSelector } from 'src/app/hooks';
import * as TutorReducer from 'src/app/reducers/room/tutor.reducer';
import { setTutorPanelOpen } from 'src/app/reducers/split-screen.reducer';
import { AI_TERMS_OF_USE_URL, AI_TUTOR_NAME, DEFAULT_TUTOR_LANDING_PAGE_URL } from 'src/common/constants';
import { useFeatureIsOn } from 'src/common/growthbook';
import { invalidateUserQuery, useGetUserQuery } from 'src/common/slices';
import { type SendTutorMessage, type SendTutorMessageVote, TUTOR_MESSAGE_MAX_LENGTH, TutorDisplayMode, TutorMessageSender } from 'src/features/room/components/tutor/tutor.types';
import { useGetRoomDetailQuery } from 'src/features/room/room.slice';
import Echo from 'src/images/svgs/echo/avatar.svg';
import { useTutorIntroMessage, useTutorMessages, useTutorNudgeEvents, useTutorSocketEvents, useTutorTracking } from './hooks';
import { getTutorIntroMessage } from './tutor.helper';
export const TutorWindow = ({
  persona = {
    name: AI_TUTOR_NAME,
    imgUrl: Echo
  },
  roomCode
}: {
  persona?: ChatPersona;
  roomCode: string;
}) => {
  const isNotificationsToggleEnabled = useFeatureIsOn('echo-toggle-nudges');
  const [showNotificationsCheckmark, setShowNotificationsCheckmark] = useState(false);
  const {
    data: roomData
  } = useGetRoomDetailQuery(roomCode);
  const roomDetails = roomData?.data;
  const roomId = roomDetails?._id;
  const {
    data: userData
  } = useGetUserQuery();
  const roomTutorPersona = roomDetails?.tutor?.persona;
  const fullName = userData?.data?.user?.fullName;
  const username = userData?.data?.user?.username;
  const userName = fullName ? fullName.split(' ')[0] : username;
  const {
    chat: {
      sessionId: tutorSessionId,
      loading: isLoading,
      restarting: isRestarting,
      disabled: isChatDisabled,
      width: chatWidth,
      displayMode,
      draggingState,
      nudgesEnabled,
      hasSeenAiTermsOfUseBanner,
      error: activeChatError
    }
  } = useAppSelector(state => state.tutor);
  const isErrorActive = !!activeChatError;
  const dispatch = useDispatch();
  const {
    messages,
    clearMessages,
    addUserMessage,
    addUserVote
  } = useTutorMessages();
  const {
    sendMessage,
    clearMessages: clearSocketMessages,
    voteMessage,
    closeTermsOfUseBanner,
    toggleNudges,
    updateDisplayMode
  } = useTutorSocketEvents({
    roomId,
    roomCode
  });
  const {
    trackTutorClose,
    trackMessageSent,
    trackVote,
    trackChatRestart,
    trackSendFeedbackClicked,
    trackTermsOfUseBannerClosed,
    trackTutorNotificationsToggle
  } = useTutorTracking(roomCode);
  const {
    introMessage
  } = useTutorIntroMessage({
    defaultConfig: getTutorIntroMessage({
      username: userName ?? ''
    }),
    roomCode,
    roomId
  });
  const {
    clearNudge
  } = useTutorNudgeEvents({
    roomId,
    roomCode
  });
  const isInputDisabled = isErrorActive || isLoading || isRestarting || isChatDisabled;
  const notificationsTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    return () => {
      if (notificationsTimeoutRef.current) {
        clearTimeout(notificationsTimeoutRef.current);
        notificationsTimeoutRef.current = null;
      }
    };
  }, []);
  const handleSendMessage = useCallback((message: string) => {
    const type = 'free-text';
    const newMessage: SendTutorMessage = {
      message,
      sender: TutorMessageSender.USER,
      type
    };
    addUserMessage(message, type);
    sendMessage(newMessage);
    trackMessageSent(newMessage);
  }, [addUserMessage, sendMessage, trackMessageSent]);
  const handleSuggestionClick = useCallback((message: string) => {
    const type = 'button';
    const newMessage: SendTutorMessage = {
      message,
      sender: TutorMessageSender.USER,
      type
    };
    addUserMessage(message, type);
    sendMessage(newMessage);
    trackMessageSent(newMessage);
  }, [addUserMessage, sendMessage, trackMessageSent]);
  const handleVoteMessage = useCallback((vote: 1 | -1, messageId: string) => {
    if (!tutorSessionId) return;
    const currentMessage = messages.find(message => message.id === messageId);
    const currentVote = currentMessage?.vote;
    const voteMessagePayload: SendTutorMessageVote = {
      tutorSessionId,
      vote,
      messageId,
      revoke: currentVote === vote
    };
    addUserVote(voteMessagePayload.vote, messageId, voteMessagePayload.revoke);
    voteMessage(voteMessagePayload);
    trackVote(voteMessagePayload);
  }, [voteMessage, trackVote, addUserVote, messages, tutorSessionId]);
  const handleCloseTutor = useCallback(() => {
    dispatch(setTutorPanelOpen(false));
    dispatch(TutorReducer.closeTutor());
    clearNudge();
    trackTutorClose();
  }, [dispatch, trackTutorClose, clearNudge]);
  const handleChatRestart = useCallback(() => {
    clearMessages();
    clearSocketMessages();
    dispatch(TutorReducer.setIntroMessageVisible(false));
    trackChatRestart();
  }, [clearMessages, clearSocketMessages, trackChatRestart, dispatch]);
  const handleSendFeedback = useCallback(() => {
    window.open('https://docs.google.com/forms/d/e/1FAIpQLSdW4GmrkrbPlP-fcH0lf8Fh_UvgiyGK4ZtuSajpIQRn9fv8-g/viewform', '_blank', 'noopener');
    trackSendFeedbackClicked();
  }, [trackSendFeedbackClicked]);
  const handleTermsOfUseBannerClose = useCallback(() => {
    closeTermsOfUseBanner();
    trackTermsOfUseBannerClosed();
    invalidateUserQuery();
  }, [closeTermsOfUseBanner, trackTermsOfUseBannerClosed]);
  const handleHeaderHover = (state: 'enter' | 'leave') => {
    if (displayMode === TutorDisplayMode.EMBEDDED || draggingState === 'dragging') return;
    if (state === 'enter') dispatch(TutorReducer.setDraggingState('drag-start'));
    if (state === 'leave') dispatch(TutorReducer.setDraggingState('idle'));
  };
  const handleNotificationsToggle = useCallback(() => {
    if (!isNotificationsToggleEnabled) return;
    if (!nudgesEnabled) {
      setShowNotificationsCheckmark(true);
      if (notificationsTimeoutRef.current) {
        clearTimeout(notificationsTimeoutRef.current);
      }
      notificationsTimeoutRef.current = setTimeout(() => {
        setShowNotificationsCheckmark(false);
        notificationsTimeoutRef.current = null;
      }, 1_000);
    }
    const newNudgesEnabled = !nudgesEnabled;
    dispatch(TutorReducer.setNudgesEnabled(newNudgesEnabled));
    toggleNudges(newNudgesEnabled);
    trackTutorNotificationsToggle(newNudgesEnabled);
    invalidateUserQuery();
  }, [isNotificationsToggleEnabled, nudgesEnabled, dispatch, toggleNudges, trackTutorNotificationsToggle]);
  const handleDisplayModeToggle = useCallback(() => {
    if (displayMode === TutorDisplayMode.FLOATING) {
      dispatch(setTutorPanelOpen(true));
      dispatch(TutorReducer.setDisplayMode(TutorDisplayMode.EMBEDDED));
      updateDisplayMode(TutorDisplayMode.EMBEDDED);
    } else {
      dispatch(setTutorPanelOpen(false));
      dispatch(TutorReducer.setDisplayMode(TutorDisplayMode.FLOATING));
      updateDisplayMode(TutorDisplayMode.FLOATING);
    }
    invalidateUserQuery();
  }, [displayMode, dispatch, updateDisplayMode]);
  const handleChatWidthToggle = useCallback(() => dispatch(TutorReducer.toggleChatExpansion()), [dispatch]);
  const HEADER_OPTIONS = [...(isNotificationsToggleEnabled && (!nudgesEnabled || showNotificationsCheckmark) ? [{
    id: 'tutor-nudges-toggle',
    label: showNotificationsCheckmark ? 'Notifications enabled' : 'Enable notifications',
    icon: {
      name: showNotificationsCheckmark ? faCheck : faBells
    },
    onClick: handleNotificationsToggle
  }] : []), {
    id: 'tutor-preferences-icon',
    label: 'Preferences',
    icon: {
      name: faEllipsis,
      rotation: 90
    },
    subOptions: [{
      id: 'tutor-restart-icon',
      label: 'Restart',
      icon: {
        name: faRedo
      },
      onClick: handleChatRestart
    }, ...(isNotificationsToggleEnabled ? [{
      id: 'tutor-toggle-notifications-icon',
      label: nudgesEnabled ? 'Snooze notifications for this room' : 'Enable notifications',
      icon: {
        name: nudgesEnabled ? faBellSlash : faBells
      },
      onClick: handleNotificationsToggle
    }] : []), {
      id: 'tutor-feedback-icon',
      label: 'Feedback',
      icon: {
        name: faThoughtBubble
      },
      onClick: handleSendFeedback
    }],
    onClick: () => {}
  }, ...(displayMode === TutorDisplayMode.FLOATING ? [{
    id: 'tutor-toggle-width-icon',
    label: chatWidth === 64 ? 'Shrink' : 'Expand',
    icon: {
      name: chatWidth === 64 ? faArrowsToDottedLine : faArrowsFromDottedLine,
      rotation: 90
    },
    onClick: handleChatWidthToggle
  }] : []), {
    id: 'tutor-close-icon',
    label: 'Minimize',
    icon: {
      name: displayMode === TutorDisplayMode.FLOATING ? faChevronDown : faArrowLeftToLine
    },
    onClick: handleCloseTutor
  }, {
    id: 'tutor-toggle-display',
    label: displayMode === TutorDisplayMode.FLOATING ? 'Embed in the screen' : 'Switch to floating',
    icon: {
      name: displayMode === TutorDisplayMode.EMBEDDED ? faArrowDownLeftAndArrowUpRightToCenter : faArrowUpRightAndArrowDownLeftFromCenter,
      rotation: 90
    },
    onClick: handleDisplayModeToggle
  }] as ChatWindowProps['headerOptions'];
  return <ChatWindow data-testid="chat-window" onCloseClick={handleCloseTutor} onSuggestionClick={handleSuggestionClick} onVotingClick={handleVoteMessage} onRestartClick={handleChatRestart} onSendMessageClick={handleSendMessage} onFeedbackClick={handleSendFeedback} onExpandClick={handleChatWidthToggle} messages={messages as unknown as ChatMessage[]} persona={roomTutorPersona ?? persona} error={activeChatError} isLoading={isLoading && !isErrorActive} isExpanded={chatWidth === 64} isInputDisabled={isInputDisabled} landingPageUrl={DEFAULT_TUTOR_LANDING_PAGE_URL} termsOfUseBanner={{
    isVisible: !hasSeenAiTermsOfUseBanner,
    content: <>
            Echo can make mistakes. Check out our{' '}
            <a href={AI_TERMS_OF_USE_URL} target="_blank" rel="noopener noreferrer">
              Terms of Use
            </a>
          </>,
    onClick: handleTermsOfUseBannerClose
  }} introMessage={introMessage} headerOptions={HEADER_OPTIONS} onHeaderHover={handleHeaderHover} displayMode={displayMode} draggingState={draggingState} isBeta askEchoPrompt="Tell me more about " maxMessageLength={TUTOR_MESSAGE_MAX_LENGTH} showCharacterCount data-sentry-element="ChatWindow" data-sentry-component="TutorWindow" data-sentry-source-file="tutor-window.tsx" />;
};